import React, {useState} from 'react';

import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import notifications from 'notifications';
import {processTaskSchemaURL} from 'processes/dao/ProcessSchemaDAO';
import {NO_ACCESS_TEXT} from 'processes/ProcessConstants';
import {
  setRequestV2DefinitionNameTemplateParts,
  UpdateRequestV2DefinitionParams,
} from 'processes/requests_v2/network/RequestV2DefinitionDAO';
import {
  REQUEST_V2_DEFINITION_PAGE_QUERY,
  updateAndCacheRequestV2Definition,
} from 'processes/requests_v2/request-v2-definition/RequestV2DefinitionPageQueryUtils';
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_REQUEST_DEFINITION_PREFIX_LENGTH,
  RequestTemplateInputGroup,
  maybeStrLengthValidationMessage,
  RequestV2DefinitionDescriptionField,
  RequestV2DefinitionPrefixField,
} from 'processes/requests_v2/request-v2-definition/RequestV2DefinitionUtils';
import RequestV2NameTemplateField from 'processes/requests_v2/request-v2-definition/RequestV2NameTemplateField';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import {omit} from 'util/omitKey';

export interface ProcessTaskSchemaDetails {
  id: string;
  name: string;
}

export interface EditRequestV2TemplateModalProps {
  onRequestClose: () => void;
  prefix: string;
  requestV2DefinitionId: string;
  description: string | null;
  processTaskSchema: ProcessTaskSchemaDetails | null;
  nameTemplateParts: Array<NameTemplatePart>;
  onNameTemplatePartsChange?: (parts: Array<NameTemplatePart>) => void;
}

export default function EditRequestV2TemplateModal(props: EditRequestV2TemplateModalProps): JSX.Element {
  const initialPrefix = props.prefix;
  const [description, setDescription] = useState(props.description ?? '');
  const [prefix, setPrefix] = useState(props.prefix);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [nameTemplateParts, setNameTemplateParts] = useState<Array<NameTemplatePart>>(
    props.nameTemplateParts
  );

  function isSubmitEnabled(): boolean {
    return (
      maybeStrLengthValidationMessage(description, MAX_DESCRIPTION_LENGTH) == null &&
      prefix.trim().length > 0 &&
      maybeStrLengthValidationMessage(prefix, MAX_REQUEST_DEFINITION_PREFIX_LENGTH) == null &&
      isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_ADMIN})
    );
  }

  function checkLength(inputStr: string, propertyName: string, maxLength: number): void {
    const maybeErrorMessage = maybeStrLengthValidationMessage(inputStr, maxLength);

    if (maybeErrorMessage != null) {
      setErrors({
        ...errors,
        [propertyName]: maybeErrorMessage,
      });
    } else {
      setErrors({...omit(errors, propertyName)});
    }
  }

  return (
    <Modal
      title="Template information"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={async () => {
        setErrors({});
        try {
          const trimmedPrefix = prefix.trim();
          const updateParams: UpdateRequestV2DefinitionParams = {
            id: props.requestV2DefinitionId,
            description: description.length > 0 ? description.trim() : undefined,
            actionOrigin: ActionOrigin.REQUEST_V2_DEFINITION_CONFIG_PAGE,
            actionMethod: ActionMethod.DIRECT_EDIT,
          };
          if (trimmedPrefix !== initialPrefix) {
            updateParams.prefix = trimmedPrefix;
          }
          await updateAndCacheRequestV2Definition(updateParams, 'Updating request template...');
          props.onRequestClose();
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (e) {
          if (e.responseJSON?.errors != null) {
            setErrors(e.responseJSON.errors);
          } else {
            notifications.notifyError(e);
          }
        }
      }}
      buttons={[
        {text: 'Cancel', onClick: props.onRequestClose},
        {text: 'Save', submit: true, disabled: !isSubmitEnabled()},
      ]}
      dataTestAttrs={{component: 'EditRequestV2TemplateModal', element: 'modal'}}
    >
      <FormGroup dataTestAttrs={{component: 'EditRequestV2TemplateModal', element: 'name'}}>
        <RequestTemplateInputGroup>
          <Label>Task Schema</Label>
          {props.processTaskSchema == null ? (
            <div>{NO_ACCESS_TEXT}</div>
          ) : (
            <div>
              <a
                href={processTaskSchemaURL(props.processTaskSchema.id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {props.processTaskSchema.name}
                &nbsp;
                <BenchlingIcon icon="arrow-up-right-to-square" className={undefined} />
              </a>
            </div>
          )}
        </RequestTemplateInputGroup>
        <RequestV2DefinitionPrefixField
          prefix={prefix}
          onChangeHandler={(e) => {
            setPrefix(e.target.value);
            checkLength(e.target.value, 'prefix', MAX_REQUEST_DEFINITION_PREFIX_LENGTH);
          }}
          errorMessage={errors?.prefix}
          dataTestComponentName="EditRequestV2TemplateModal"
        />
        <RequestV2DefinitionDescriptionField
          description={description}
          onChangeHandler={(e) => {
            setDescription(e.target.value);
            checkLength(e.target.value, 'description', MAX_DESCRIPTION_LENGTH);
          }}
          errorMessage={errors?.description}
          dataTestComponentName="EditRequestV2TemplateModal"
        />
        <RequestV2NameTemplateField
          definitionId={props.requestV2DefinitionId}
          nameTemplateParts={nameTemplateParts}
          isWritable={isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_ADMIN})}
          setNameTemplateParts={async (parts) => {
            try {
              await setRequestV2DefinitionNameTemplateParts({
                definitionId: props.requestV2DefinitionId,
                nameTemplateParts: parts,
              });
              setNameTemplateParts(parts);
              props.onNameTemplatePartsChange?.(parts);
              getApolloClient().refetchQueries({include: [REQUEST_V2_DEFINITION_PAGE_QUERY]});
              return true;
            } catch (e) {
              notifications.notifyError(e);
              throw e;
            }
          }}
        />
      </FormGroup>
    </Modal>
  );
}
